import { LayoutService } from '@/app/layout/service/layout.service';
import { CommonModule } from '@angular/common';
import { booleanAttribute, Component, computed, inject, Input, model, OnInit, PLATFORM_ID, Signal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { PrimeNG } from 'primeng/config';
import { DrawerModule } from 'primeng/drawer';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { THEME_PRESETS, SURFACE_PALETTES, SurfacePalette, getPrimaryPalettes, surfaceSwatchColor } from '@/app/layout/service/theme.constants';

@Component({
    selector: 'app-configurator',
    standalone: true,
    imports: [CommonModule, FormsModule, SelectButtonModule, DrawerModule, ToggleSwitchModule, RadioButtonModule],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './app.configurator.html'
})
/* v8 ignore start */
export class AppConfigurator implements OnInit {
/* v8 ignore stop */
    @Input({ transform: booleanAttribute }) simple: boolean = false;
    @Input() location: string = 'app';

    router = inject(Router);
    config = inject(PrimeNG);
    layoutService = inject(LayoutService);
    platformId = inject(PLATFORM_ID);
    primeng = inject(PrimeNG);

    presets = Object.keys(THEME_PRESETS);

    themeOptions = [
        { name: 'Light', value: false },
        { name: 'Dark', value: true }
    ];

    menuThemeOptions: { name: string; value: string }[] = [];

    surfaces: SurfacePalette[] = SURFACE_PALETTES;
    readonly surfaceSwatchColor = surfaceSwatchColor;

    selectedPrimaryColor = computed(() => this.layoutService.layoutConfig().primary);
    selectedSurfaceColor = computed(() => this.layoutService.layoutConfig().surface);
    selectedPreset = computed(() => this.layoutService.layoutConfig().preset);
    menuMode = model(this.layoutService.layoutConfig().menuMode);
    cardStyle = model(this.layoutService.layoutConfig().cardStyle);
    visible: Signal<boolean> = computed(() => this.layoutService.layoutState().configSidebarVisible);
    darkTheme = computed(() => this.layoutService.layoutConfig().darkTheme);
    selectedSurface = computed(() => this.layoutService.layoutConfig().surface);
    menuTheme = computed(() => this.layoutService.layoutConfig().menuTheme);

    cardStyleOptions = [
        { name: 'Transparent', value: 'transparent' },
        { name: 'Filled', value: 'filled' }
    ];

    primaryColors = computed<SurfacePalette[]>(() => {
        return getPrimaryPalettes(this.layoutService.layoutConfig().preset);
    });

    ngOnInit() {
        this.updateMenuThemeOptions();
    }

    updateMenuThemeOptions() {
        if (this.darkTheme()) {
            this.menuThemeOptions = [
                { name: 'Dark', value: 'dark' },
                { name: 'Primary', value: 'primary' }
            ];
        } else {
            this.menuThemeOptions = [
                { name: 'Light', value: 'light' },
                { name: 'Dark', value: 'dark' },
                { name: 'Primary', value: 'primary' }
            ];
        }
    }

    updateColors(event: Event, type: 'primary' | 'surface', color: SurfacePalette) {
        if (type === 'primary') {
            this.layoutService.updateConfig({ primary: color.name }, true);
        } else if (type === 'surface') {
            this.layoutService.updateConfig({ surface: color.name }, true);
        }

        event.stopPropagation();
    }

    onPresetChange(preset: string) {
        this.layoutService.updateConfig({ preset }, true);
    }

    onDrawerHide() {
        this.layoutService.layoutState.update((prev) => ({ ...prev, configSidebarVisible: false }));
    }

    onCardStyleChange(value: string) {
        this.layoutService.updateConfig({ cardStyle: value }, true);
    }

    onMenuThemeChange(value: string) {
        this.layoutService.updateConfig({ menuTheme: value }, true);
    }

    setMenuMode(mode: string) {
        this.menuMode.set(mode);
        this.layoutService.changeMenuMode(mode);
    }

    toggleDarkMode() {
        const nextDark = !this.layoutService.layoutConfig().darkTheme;
        const newMenuTheme = nextDark ? 'dark' : this.layoutService.layoutConfig().menuTheme;
        this.layoutService.updateConfig(
            {
                darkTheme: nextDark,
                menuTheme: newMenuTheme
            },
            true
        );
        this.updateMenuThemeOptions();
    }

    toggleConfigSidebar() {
        this.layoutService.layoutState.update((val) => ({ ...val, configSidebarVisible: !val.configSidebarVisible }));
    }
}
